import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import { Bone } from '../../../../components/ui/DashboardSkeletons';
import { API, OrgDocument } from '../../../../api';
import { events } from '../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../utils/amplitudeProductTracking';
import { getUserDetails } from '../../../../utils/orgName';
import ROLES from '../../../../utils/roles';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../redux/hooks';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import ElliottFace from '../../components/ElliottFace';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArticleIcon from '@mui/icons-material/Article';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { toast } from 'react-toastify';
import AddFileModal from '../../../../components/admin3/newProgram/masterclass/ai/AddFileModal';
import AddTextModal from '../../../../components/admin3/newProgram/masterclass/ai/AddTextModal';
import AddURLModal from '../../../../components/admin3/newProgram/masterclass/ai/AddURLModal';
// Elliott Conversations shares the Elliott lesson's chat language — same tokens,
// same sender treatment (orb + ELLIOTT label / outlined learner bubble), same
// input bar — so the two Elliott surfaces read as one experience.
import { elliottTokens as t } from '../home/elliott-lesson/tokens';
import ElliottMessage, { ElliottAvatar } from '../home/elliott-lesson/components/ElliottMessage';
import LearnerMessage from '../home/elliott-lesson/components/LearnerMessage';
import TypingDots from '../home/elliott-lesson/components/TypingDots';
import InputBar from '../home/elliott-lesson/components/InputBar';
import ElliottProfileDialog from './ElliottProfileDialog';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';

/* ──────────────────────────── Types ──────────────────────────── */

type ThumbRating = 'up' | 'down' | null;

type CharlieMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  logId?: string;
  thumbRating?: ThumbRating;
};

type CharlieConversation = {
  convId?: string;
  convid?: string;
  convTitle?: string;
  title?: string;
  description?: string;
  summary?: string;
  updatedAt?: string | number;
  createdAt?: string | number;
  date?: string;
};

/**
 * Backend stages 1–7 (the stage the BE waits on next), status true = finished.
 * 1: intro, loops until the AI classifies the reply as "Agreement".
 * 4: the learner's role. 5: their goals, after which Elliott shows what it will
 * remember. 6: "Looks great!" or a correction (loops). 7: Sources, then done.
 */
type OnboardingStage = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

/* ──────────────────────────── Markdown renderer ──────────────────────────── */

const linkStyle: React.CSSProperties = {
  color: t.brandLight,
  fontWeight: 600,
  textDecoration: 'underline',
  textUnderlineOffset: 2
};

/**
 * Lightweight markdown renderer for Charlie messages.
 * Supports: [links](target), **bold**, *italic*, `code`, unordered lists
 * (- item), ordered lists (1. item), and line breaks.
 */
const renderFormattedText = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listBuffer: { type: 'ul' | 'ol'; items: { node: React.ReactNode; indent: number }[] } | null = null;
  let key = 0;

  const flushList = () => {
    if (!listBuffer) return;
    const ListTag = listBuffer.type === 'ul' ? 'ul' : 'ol';
    elements.push(
      <Box
        key={`list-${key++}`}
        component={ListTag}
        sx={{
          pl: 2.5,
          my: 0.5,
          // The global CSS reset strips list markers (list-style: none), so the
          // items indented but showed no bullets — set the markers explicitly.
          listStyleType: listBuffer.type === 'ul' ? 'disc' : 'decimal',
          listStylePosition: 'outside',
          '& li': {
            display: 'list-item',
            listStyleType: 'inherit',
            fontSize: t.bodySize,
            lineHeight: `${t.bodyLineHeight}px`,
            color: t.textPrimary,
            mb: 0.25
          },
          '& li::marker': { color: t.textTertiary }
        }}
      >
        {listBuffer.items.map((item, i) => (
          <li key={i} style={item.indent ? { marginLeft: item.indent * 20 } : undefined}>
            {item.node}
          </li>
        ))}
      </Box>
    );
    listBuffer = null;
  };

  const formatInline = (line: string): React.ReactNode => {
    // Split by [links](target), **bold**, *italic*, and `code` markers
    const parts: React.ReactNode[] = [];
    const regex = /(\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }

      if (match[2]) {
        // [label](target): Elliott links a recommended course as
        // course:<grpId>, opened in-app; web links open in a new tab; any
        // other target (e.g. javascript:) renders as its label only.
        const label = formatInline(match[2]);
        const courseId = /^course:([0-9a-fA-F]{24})$/.exec(match[3])?.[1];
        if (courseId) {
          parts.push(
            <RouterLink key={`l-${key++}`} to={`/app/home/course/${courseId}`} style={linkStyle}>
              {label}
            </RouterLink>
          );
        } else if (/^https?:\/\//i.test(match[3])) {
          parts.push(
            <a key={`l-${key++}`} href={match[3]} target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {label}
            </a>
          );
        } else {
          parts.push(<span key={`l-${key++}`}>{label}</span>);
        }
      } else if (match[4]) {
        // **bold**
        parts.push(
          <Box key={`b-${key++}`} component="span" sx={{ fontWeight: 700 }}>
            {match[4]}
          </Box>
        );
      } else if (match[5]) {
        // *italic*
        parts.push(
          <Box key={`i-${key++}`} component="span" sx={{ fontStyle: 'italic' }}>
            {match[5]}
          </Box>
        );
      } else if (match[6]) {
        // `code`
        parts.push(
          <Box
            key={`c-${key++}`}
            component="code"
            sx={{
              px: 0.5,
              py: 0.25,
              borderRadius: '3px',
              backgroundColor: 'rgba(255,255,255,0.08)',
              fontSize: 14,
              fontFamily: 'monospace'
            }}
          >
            {match[6]}
          </Box>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // "-", "•", "*" and the nested-bullet glyphs all start a bullet (Sources lists use them).
    const ulMatch = line.match(/^(\s*)[-•*◦▪‣]\s+(.+)/);
    const olMatch = line.match(/^\s*\d+[.)]\s+(.+)/);
    const isHr = /^\s*([-*_])\1{2,}\s*$/.test(line);

    if (isHr) {
      flushList();
      elements.push(
        <Box
          key={`hr-${key++}`}
          sx={{ my: 1, borderTop: '1px solid rgba(255,255,255,0.12)' }}
        />
      );
    } else if (ulMatch) {
      // Indent level from leading whitespace (2 spaces ≈ one level), capped.
      const indent = Math.min(Math.floor(ulMatch[1].replace(/\t/g, '  ').length / 2), 3);
      if (listBuffer?.type !== 'ul') {
        flushList();
        listBuffer = { type: 'ul', items: [] };
      }
      listBuffer!.items.push({ node: formatInline(ulMatch[2]), indent });
    } else if (olMatch) {
      if (listBuffer?.type !== 'ol') {
        flushList();
        listBuffer = { type: 'ol', items: [] };
      }
      listBuffer!.items.push({ node: formatInline(olMatch[1]), indent: 0 });
    } else {
      flushList();
      if (line.trim() === '') {
        elements.push(<Box key={`br-${key++}`} sx={{ height: 12 }} />);
      } else {
        elements.push(
          <Typography
            key={`p-${key++}`}
            component="span"
            sx={{
              display: 'block',
              fontSize: t.bodySize,
              lineHeight: `${t.bodyLineHeight}px`,
              color: t.textPrimary,
              fontWeight: 400,
              // Match the lesson thread: keep the sender's own line breaks/spacing.
              overflowWrap: 'break-word'
            }}
          >
            {formatInline(line)}
          </Typography>
        );
      }
    }
  }

  flushList();
  return <>{elements}</>;
};

/* ──────────────────────────── Charlie thinking loader ──────────────────────────── */

const CHARLIE_LOADING_PHRASES = [
  'Converting ambiguity into momentum...',
  'Distilling confusion into signal...',
  'Manufacturing clarity at scale...',
  'Translating noise into insight...',
  'Transforming scattered inputs into intent...',
  'Reassembling fragmented context...',
  'Compressing complexity into action...',
];

const CharlieThinkingLoader = () => {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const phrase = CHARLIE_LOADING_PHRASES[phraseIndex];
    let charIndex = 0;
    setDisplayedText('');
    const typeTimer = setInterval(() => {
      charIndex++;
      setDisplayedText(phrase.slice(0, charIndex));
      if (charIndex >= phrase.length) clearInterval(typeTimer);
    }, 28);
    return () => clearInterval(typeTimer);
  }, [phraseIndex]);

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setFading(false);
        setPhraseIndex((i) => (i + 1) % CHARLIE_LOADING_PHRASES.length);
      }, 350);
    }, 2000);
    return () => clearTimeout(fadeTimer);
  }, [phraseIndex]);

  // Same shape as an Elliott message in the lesson thread (orb + typing dots),
  // with Charlie's rotating phrase kept as the sub-line.
  return (
    <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start', width: '100%' }}>
      <ElliottAvatar />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', py: '12px' }}>
        <TypingDots />
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: t.textSecondary,
            opacity: fading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            minHeight: '20px',
            lineHeight: '20px'
          }}
        >
          {displayedText}
        </Box>
      </Box>
    </Box>
  );
};

/* ──────────────────────────── Component ──────────────────────────── */

const Charlie = () => {
  const navigate = useNavigate();
  const isMobileApp = useIsMobileApp();
  const routerLocation = useLocation();
  const { convid: routeConvId } = useParams<{ convid?: string }>();

  /* ── Redux selectors ── */
  const profileLoading = useAppSelector((state) => state.userProfile.loading);
  const lastseenGroupId = useAppSelector((state) => state.userProfile.data?.lastseen?.id) || '';
  const { location, isPlatformAdmin } = useProgramContext();

  /* ── Core UI state ── */
  const [view, setView] = useState<'loading' | 'onboarding' | 'index' | 'chat'>('loading');
  const [conversations, setConversations] = useState<CharlieConversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [messages, setMessages] = useState<CharlieMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  /* ── Onboarding state ── */
  const [onboardingStage, setOnboardingStage] = useState<OnboardingStage>(0);
  const [onboardingConvId, setOnboardingConvId] = useState<string | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  // Stage 1 is a two-step intro: the welcome shows first, and the "here's what
  // we'll cover" message (delivered as nextStageContext while still at stage 1)
  // is held here until the user clicks continue, instead of showing both at once.
  const [pendingIntroContext, setPendingIntroContext] = useState<string | null>(null);

  /* ── Chat state (post-onboarding) ── */
  const [activeConvId, setActiveConvId] = useState<string | null>(null);

  /* ── "What Elliott knows about me" ── */
  const [showProfile, setShowProfile] = useState(false);

  /* ── Sources panel ── */
  const [showSources, setShowSources] = useState(false);
  const [sourcesOrigin, setSourcesOrigin] = useState<'onboarding' | null>(null);
  const [orgDocs, setOrgDocs] = useState<OrgDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [openTextModal, setOpenTextModal] = useState(false);
  const [openUrlModal, setOpenUrlModal] = useState(false);
  const [pendingDocs, setPendingDocs] = useState<{ id: string; name: string; type: 'file' | 'text' | 'webpage' }[]>([]);
  const lastUploadedId = useRef<string | null>(null);
  const addedSourceIdsRef = useRef<Set<string>>(new Set());
  // convId of a conversation whose transcript this tab already owns in state —
  // set when we create one locally (first message of a new chat) and kept until
  // the user opens a different conversation. sendChatMessage navigates to
  // /app/charlie/<id> to update the URL, and the route effect would otherwise
  // refetch history for a conversation the backend may not have finished
  // persisting and replace the reply we just rendered (it flashed, then vanished).
  // A ref that survives re-runs of the effect — not a one-shot flag — is what
  // keeps that from happening on every later re-run too (e.g. profile refetch).
  const locallyOwnedConvIdRef = useRef<string | null>(null);
  // Guards against overlapping/stale history loads clobbering a newer transcript.
  const historyRequestRef = useRef(0);
  // Latest messages + active conversation, readable from async callbacks without
  // re-creating them on every keystroke.
  const messagesRef = useRef<CharlieMessage[]>([]);
  const activeConvIdRef = useRef<string | null>(null);
  // A question handed over from another surface (the Elliott orb, a prompt
  // chip) via navigate(route, { state: { elliottInitialMessage } }). Held here
  // until the resume call confirms onboarding is done, then sent as the first
  // message of a brand-new conversation.
  const seededQuestionRef = useRef<string | null>(null);

  /* ── Disabled action buttons (prevent double-click) ── */
  const [disabledActionButtons, setDisabledActionButtons] = useState<Record<string, boolean>>({});

  /* ── Refs ── */
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const lastResponseRef = useRef<HTMLDivElement | null>(null);
  const prevIsSendingRef = useRef(false);

  const messageList = useMemo(() => messages, [messages]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    activeConvIdRef.current = activeConvId;
  }, [activeConvId]);

  /* ────────────────── Seeded question from another surface ────────────────── */

  // Read it once on mount and strip it from the history entry, so a refresh or
  // a back-navigation doesn't ask the same question a second time.
  useEffect(() => {
    const seeded = (routerLocation.state as { elliottInitialMessage?: string } | null)?.elliottInitialMessage;
    if (!seeded?.trim()) return;
    seededQuestionRef.current = seeded.trim();
    navigate(routerLocation.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ────────────────── Initial route: decide view ────────────────── */

  useEffect(() => {
    if (profileLoading) return;
    let cancelled = false;

    if (routeConvId) {
      setOnboardingComplete(true);
      setView('chat');
      // Skip the refetch while this tab still owns the transcript locally — the
      // messages are already in state and the backend may not return them yet.
      if (locallyOwnedConvIdRef.current !== routeConvId) {
        locallyOwnedConvIdRef.current = null;
        loadConversationHistory(routeConvId);
      }
      return;
    }

    // Back at the index (no conversation in the URL) — drop local ownership so
    // reopening a conversation reads the server copy.
    locallyOwnedConvIdRef.current = null;

    const init = async () => {
      try {
        const resumeRes = await API.charlieResume({ location });
        if (cancelled) return;
        const payload = resumeRes?.data?.data ?? resumeRes?.data ?? {};

        if (payload.onboardingComplete) {
          setOnboardingComplete(true);
          const seeded = seededQuestionRef.current;
          if (seeded) {
            // Land straight on the answer: new conversation, question already
            // sent, thinking indicator running while it comes back.
            seededQuestionRef.current = null;
            handleStartNewConversation(seeded);
          } else {
            loadConversationList();
          }
        } else {
          // Onboarding still owes us answers — it takes priority over any
          // seeded question, which is dropped rather than queued behind it.
          seededQuestionRef.current = null;
          const stage = (
            typeof payload.stage === 'number'
              ? payload.stage
              : Number(payload.stage) || 0
          ) as OnboardingStage;
          setOnboardingStage(stage);

          if (payload.convId || payload.convid) {
            setOnboardingConvId(payload.convId || payload.convid);
          }

          const contextMessages: CharlieMessage[] = [];

          // Prior onboarding transcript (welcome → role prompt → the user's answer
          // → Charlie's reply) so a refresh restores the full scrollback instead of
          // only the current stage's prompt.
          const history: { role?: string; content?: string }[] = Array.isArray(payload.history) ? payload.history : [];
          history.forEach((item, idx) => {
            const content = (item?.content || '').toString();
            if (!content) return;
            contextMessages.push({
              id: `resume-history-${idx}-${Date.now()}`,
              role: item?.role === 'user' ? 'user' : 'assistant',
              content
            });
          });

          if (payload.response) {
            contextMessages.push({
              id: `resume-response-${Date.now()}`,
              role: 'assistant',
              content: payload.response
            });
          }
          // Stage 1 delivers the intro in two parts (welcome as `response`, "here's
          // what we'll cover" as `nextStageContext`); gate the second behind the
          // welcome's continue button. Later stages show their single prompt directly.
          const deferIntro = stage === 1 && !!payload.response;
          if (payload.nextStageContext && !deferIntro) {
            contextMessages.push({
              id: `resume-context-${Date.now()}`,
              role: 'assistant',
              content: payload.nextStageContext
            });
          }
          if (contextMessages.length) {
            setMessages(contextMessages);
          }
          if (deferIntro && payload.nextStageContext) {
            setPendingIntroContext(payload.nextStageContext);
          }

          setView('onboarding');
        }
      } catch {
        if (cancelled) return;
        setView('onboarding');
        // Resume failed (e.g. network error) — fall back to the intro by sending
        // no input, which the backend treats as a read-only "show me the intro".
        triggerOnboardingStage(undefined, { silent: true });
      }
    };

    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, routeConvId]);

  /* ────────────────── Auto-scroll ────────────────── */

  useEffect(() => {
    if (view !== 'chat' && view !== 'onboarding') return;
    const responseJustArrived = prevIsSendingRef.current && !isSending;
    prevIsSendingRef.current = isSending;
    const raf = requestAnimationFrame(() => {
      if (responseJustArrived && lastResponseRef.current) {
        lastResponseRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        chatBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [messageList.length, isSending, view]);

  /* ────────────────── Conversation list loader ────────────────── */

  const loadConversationList = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const response = await API.charlieGetConversations({ location });
      const raw = response?.data?.data?.data ?? response?.data?.data ?? response?.data ?? [];
      const list = Array.isArray(raw) ? raw : [];

      const normalized = list
        .flatMap((item: any) => {
          if (item && typeof item === 'object' && Array.isArray(item.convIds)) {
            return item.convIds;
          }
          return item;
        })
        .filter(
          (item: any) =>
            item &&
            typeof item === 'object' &&
            ('convId' in item || 'convid' in item || 'convTitle' in item || 'title' in item)
        );

      setConversations(normalized);
      setView('index');
    } catch {
      setConversations([]);
      setView('index');
    } finally {
      setIsLoadingConversations(false);
    }
  }, [location]);

  /* ────────────────── Load single conversation history ────────────────── */

  const loadConversationHistory = useCallback(
    async (convId: string) => {
      const requestId = ++historyRequestRef.current;
      // Deliberately re-reading from the server — this tab no longer owns any
      // transcript locally.
      locallyOwnedConvIdRef.current = null;
      setIsLoadingConversations(true);
      try {
        const response = await API.charlieGetConversations({ convId, location });
        // A newer load started while this one was in flight — its result wins.
        if (requestId !== historyRequestRef.current) return;
        const payload = response?.data?.data ?? response?.data ?? {};
        const history: any[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.messages)
            ? payload.messages
            : [];

        const historyMessages: CharlieMessage[] = history
          .map((item: any, index: number) => ({
            id: `history-${convId}-${index}`,
            role: (
              typeof item.role === 'string' &&
              (item.role.toLowerCase() === 'assistant' || item.role.toLowerCase() === 'charlie')
                ? 'assistant'
                : 'user'
            ) as CharlieMessage['role'],
            content: (item.content || '') as string,
            logId: item.logId || undefined,
            thumbRating: (item.thumbRating as ThumbRating) || null
          }))
          .filter((message: CharlieMessage) => Boolean(message.content));

        // Never trade a fuller local transcript for a shorter server copy of the
        // SAME conversation — the backend persists the last exchange
        // asynchronously, so a reload landing in that window used to blank out
        // Elliott's just-rendered reply. Switching conversations always replaces.
        const sameConversation = activeConvIdRef.current === convId;
        const staleServerCopy =
          sameConversation && historyMessages.length < messagesRef.current.length;
        if (!staleServerCopy) {
          setMessages(historyMessages);
        }
        setActiveConvId(convId);
        setView('chat');
      } catch {
        // Leave whatever is on screen rather than blanking the conversation.
        if (requestId === historyRequestRef.current) setView('chat');
      } finally {
        if (requestId === historyRequestRef.current) setIsLoadingConversations(false);
      }
    },
    [location]
  );

  /* ────────────────── Onboarding: trigger a stage (POST without meaningful input) ────────────────── */

  const triggerOnboardingStage = useCallback(
    async (userInput?: string, opts?: { silent?: boolean }) => {
      setIsSending(true);

      // Show user message bubble unless silent (e.g. auto-trigger "hi" on init)
      if (userInput && !opts?.silent) {
        setMessages((prev) => [
          ...prev,
          { id: `${Date.now()}-user`, role: 'user', content: userInput }
        ]);
      }

      try {
        const response = await API.charlieSendMessage({
          input: userInput || undefined,
          convId: onboardingConvId || undefined,
          location,
          grpId: lastseenGroupId || undefined
        });

        const payload = response?.data?.data?.data ?? response?.data?.data ?? response?.data ?? {};
        const stageValue = typeof payload.stage === 'number' ? payload.stage : Number(payload.stage);

        if (!Number.isNaN(stageValue)) {
          setOnboardingStage(stageValue as OnboardingStage);
        }

        const responseConvId = payload.convId || payload.convid;
        if (responseConvId) {
          setOnboardingConvId(responseConvId);
        }

        // Backend returns response (AI answer) and nextStageContext (Charlie's next prompt)
        const responseText = payload.response || '';
        const nextStageText = payload.nextStageContext || '';
        const sameContent =
          !!responseText && responseText.trim() === nextStageText.trim();
        // On the scripted intro presentation (no user input, still at stage 1) the
        // nextStageContext is the second intro message and must stay gated behind a
        // continue click. Once the user has typed/clicked, show replies inline.
        const deferIntro = !userInput && stageValue === 1 && !!nextStageText;

        const matchesLastAssistant = (prev: CharlieMessage[], content: string) => {
          const trimmed = content.trim();
          if (!trimmed) return false;
          for (let i = prev.length - 1; i >= 0; i--) {
            const m = prev[i];
            if (m.role !== 'assistant') continue;
            return (m.content || '').trim() === trimmed;
          }
          return false;
        };

        if (responseText) {
          setMessages((prev) =>
            matchesLastAssistant(prev, responseText)
              ? prev
              : [
                  ...prev,
                  {
                    id: `${Date.now()}-ai-response`,
                    role: 'assistant',
                    content: responseText,
                    logId: payload.logId
                  }
                ]
          );
        }

        if (deferIntro) {
          setPendingIntroContext(nextStageText);
        } else {
          // Past the gated welcome — drop any deferred intro step and show inline.
          setPendingIntroContext(null);
          if (nextStageText && !sameContent) {
            setMessages((prev) =>
              matchesLastAssistant(prev, nextStageText)
                ? prev
                : [
                    ...prev,
                    {
                      id: `${Date.now()}-assistant-context`,
                      role: 'assistant',
                      content: nextStageText
                    }
                  ]
            );
          }
        }

        // The last stage answers with status true: onboarding is complete
        if (payload.status === true) {
          handleOnboardingFinished();
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            id: `${Date.now()}-assistant-error`,
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.'
          }
        ]);
      } finally {
        setIsSending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onboardingConvId, location]
  );

  /* ────────────────── Auto-advance scripted onboarding stages ────────────────── */

  useEffect(() => {
    if (view !== 'onboarding' || isSending) return;
    if (onboardingStage === 0 || onboardingStage === 2) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.id?.endsWith('-assistant-error')) return;
      const timer = setTimeout(() => {
        triggerOnboardingStage(undefined, { silent: true });
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [view, onboardingStage, isSending, triggerOnboardingStage, messages]);

  /* ────────────────── Onboarding: send user input ────────────────── */

  const sendOnboardingMessage = useCallback(
    async (text: string, opts?: { disableKey?: string }) => {
      if (isSending) return;
      const trimmedText = text.trim();
      if (!trimmedText) return;

      if (opts?.disableKey) {
        setDisabledActionButtons((prev) => ({ ...prev, [opts.disableKey as string]: true }));
      }
      setInput('');
      await triggerOnboardingStage(trimmedText);
    },
    [isSending, triggerOnboardingStage]
  );

  /* ────────────────── Onboarding: reveal the deferred second intro message ────────────────── */

  // The intro's "here's what we'll cover" message is held in pendingIntroContext
  // so the welcome's continue button reveals it locally (no backend call). The
  // revealed message carries its own "Are you ready to jump in?" gate, whose
  // click then sends the real agreement to advance the stage.
  const revealPendingIntro = useCallback(() => {
    if (!pendingIntroContext) return;
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-intro-step`, role: 'assistant', content: pendingIntroContext }
    ]);
    setPendingIntroContext(null);
  }, [pendingIntroContext]);

  /* ────────────────── Onboarding finished ────────────────── */

  const handleOnboardingFinished = useCallback(() => {
    setOnboardingComplete(true);
    // Transfer onboarding convId so the user can keep chatting in the same conversation
    if (onboardingConvId) {
      setActiveConvId(onboardingConvId);
    }
    trackWithBaseProps(events.copilotIntroCompleted, { convId: onboardingConvId || '' });
  }, [onboardingConvId]);

  /* ────────────────── Post-onboarding: send chat message ────────────────── */

  const sendChatMessage = useCallback(
    async (text: string) => {
      if (isSending) return;
      const trimmedText = text.trim();
      if (!trimmedText) return;

      setIsSending(true);
      setInput('');
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-user`, role: 'user', content: trimmedText }
      ]);

      // Fired on send (the user action), regardless of how the request lands.
      trackWithBaseProps(events.copilotQueried, { convId: activeConvId || 'new' });

      try {
        const response = await API.charlieSendMessage({
          input: trimmedText,
          convId: activeConvId || undefined,
          location,
          grpId: lastseenGroupId || undefined
        });

        const payload = response?.data?.data ?? response?.data ?? {};
        const responseConvId = payload.convId || payload.convid;
        if (responseConvId) {
          setActiveConvId(responseConvId);
          // Update URL if this is a new conversation
          if (!activeConvId) {
            // Mark as locally owned so the route effect doesn't refetch (and wipe)
            // the transcript we already have when the URL changes.
            locallyOwnedConvIdRef.current = responseConvId;
            navigate(`/app/charlie/${responseConvId}`, { replace: true });
          }
        }

        const responseText = payload.response || '';
        if (responseText) {
          setMessages((prev) => [
            ...prev,
            {
              id: `${Date.now()}-assistant`,
              role: 'assistant',
              content: responseText,
              logId: payload.logId
            }
          ]);
        }

        // Copilot grounded this answer on Vector-DB content (retrievedCount from the BE).
        if (typeof payload.retrievedCount === 'number' && payload.retrievedCount > 0) {
          trackWithBaseProps(events.copilotRetrieved, {
            convId: responseConvId || activeConvId || '',
            logId: payload.logId || '',
            retrievedCount: payload.retrievedCount
          });
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            id: `${Date.now()}-assistant-error`,
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.'
          }
        ]);
      } finally {
        setIsSending(false);
      }
    },
    [activeConvId, isSending, location, navigate]
  );

  /* ────────────────── Thumb rating ────────────────── */

  const handleThumbRating = useCallback(
    async (messageId: string, logId: string, rating: 'up' | 'down') => {
      const previousRating = messages.find((m) => m.id === messageId)?.thumbRating ?? null;
      const newRating = previousRating === rating ? null : rating;

      setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, thumbRating: newRating } : msg)));

      if (newRating === null) return;

      try {
        await API.charlieRateMessage({ logId, rating: newRating, location });
        const thumbEvent = newRating === 'up' ? events.thumbRatedUp : events.thumbsRatedDown;
        trackWithBaseProps(thumbEvent, { logId });
      } catch {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, thumbRating: previousRating } : msg))
        );
      }
    },
    [messages]
  );

  /* ────────────────── Open existing conversation from list ────────────────── */

  const openConversation = useCallback(
    async (conversation: CharlieConversation) => {
      const id = conversation.convId || conversation.convid;
      if (!id) return;
      navigate(`/app/charlie/${id}`);
    },
    [navigate]
  );

  /* ────────────────── Start new conversation from index ────────────────── */

  const handleStartNewConversation = useCallback(
    (text?: string) => {
      setMessages([]);
      setActiveConvId(null);
      activeConvIdRef.current = null;
      locallyOwnedConvIdRef.current = null;
      setView('chat');
      if (text?.trim()) {
        sendChatMessage(text);
      }
    },
    [sendChatMessage]
  );

  /* ────────────────── Format date ────────────────── */

  const formatConversationDate = (conversation: CharlieConversation) => {
    const rawValue = conversation.updatedAt ?? conversation.createdAt ?? conversation.date;
    if (!rawValue) return '';
    const dateValue = typeof rawValue === 'number' ? new Date(rawValue) : new Date(rawValue);
    if (Number.isNaN(dateValue.getTime())) {
      return typeof rawValue === 'string' ? rawValue : '';
    }
    return dateValue.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  /* ────────────────── Sources panel logic ────────────────── */

  const fetchDocuments = useCallback(
    async (withLoader = true) => {
      if (withLoader) setLoadingDocs(true);
      setDocsError(null);
      try {
        const response = await API.getOrganizationDocuments(lastseenGroupId);
        const docs = response.documents || [];
        setOrgDocs(docs);

        if (pendingDocs.length) {
          setPendingDocs((prev) =>
            prev.filter(
              (pending) => !docs.some((doc) => doc._id === pending.id)
            )
          );
        }

        if (lastUploadedId.current) {
          const matched = docs.find(
            (doc) => doc._id === lastUploadedId.current && doc.documentProcessingStatus === 'indexed'
          );
          if (matched) lastUploadedId.current = null;
        }
        return docs;
      } catch (err) {
        console.error('Failed to load documents', err);
        setDocsError('Unable to load documents. Please try again.');
        return [];
      } finally {
        if (withLoader) setLoadingDocs(false);
      }
    },
    [pendingDocs.length, lastseenGroupId]
  );

  useEffect(() => {
    if (!showSources) return;
    fetchDocuments();
  }, [fetchDocuments, showSources]);

  useEffect(() => {
    if (!showSources || !lastseenGroupId || pendingDocs.length === 0) return undefined;
    const interval = setInterval(() => fetchDocuments(false), 5000);
    return () => clearInterval(interval);
  }, [fetchDocuments, pendingDocs.length, lastseenGroupId, showSources]);

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!lastseenGroupId) {
        toast.error('No workspace selected. Please build or join a program first.');
        return;
      }
      try {
        const response = await API.processDocument(file, lastseenGroupId);
        const docId = response?.data?.id;
        if (!docId) throw new Error('Missing document id');
        lastUploadedId.current = docId;
        addedSourceIdsRef.current.add(docId);
        setPendingDocs((prev) => [...prev, { id: docId, name: file.name, type: 'file' }]);
        toast.success("File uploaded. We're indexing it now which will take a few minutes.");
        await fetchDocuments(false);
      } catch (err) {
        console.error('File upload error', err);
        toast.error('The file could not be uploaded. Try again.');
        throw err;
      }
    },
    [fetchDocuments, lastseenGroupId]
  );

  const handleAddText = useCallback(
    async (title: string, body: string) => {
      if (!lastseenGroupId) {
        toast.error('No workspace selected. Please build or join a program first.');
        return;
      }
      try {
        const response = await API.processTextDocument({
          title,
          content: body,
          groupId: lastseenGroupId
        });
        const docId = response?.data?.id;
        if (!docId) throw new Error('Missing text document id');
        lastUploadedId.current = docId;
        addedSourceIdsRef.current.add(docId);
        setPendingDocs((prev) => [...prev, { id: docId, name: title, type: 'text' }]);
        toast.success("Text added. We're indexing it now which will take a few minutes.");
        await fetchDocuments(false);
      } catch (err) {
        console.error('Add text error', err);
        toast.error('The text could not be saved. Try again.');
        throw err;
      }
    },
    [fetchDocuments, lastseenGroupId]
  );

  const handleAddUrl = useCallback(
    async (title: string, url: string) => {
      if (!lastseenGroupId) {
        toast.error('No workspace selected. Please build or join a program first.');
        return;
      }
      try {
        const response = await API.processWebsiteDocument({
          title,
          websiteUrl: url,
          groupId: lastseenGroupId
        });
        const docId = response?.data?.id;
        if (!docId) throw new Error('Missing website document id');
        lastUploadedId.current = docId;
        addedSourceIdsRef.current.add(docId);
        setPendingDocs((prev) => [...prev, { id: docId, name: title, type: 'webpage' }]);
        toast.success("URL added. We're indexing it now which will take a few minutes.");
        await fetchDocuments(false);
      } catch (err) {
        console.error('Add URL error', err);
        toast.error('The URL could not be saved. Try again.');
        throw err;
      }
    },
    [fetchDocuments, lastseenGroupId]
  );

  const handleSourcesDone = useCallback(() => {
    const count = addedSourceIdsRef.current.size;
    addedSourceIdsRef.current = new Set();
    setSourcesOrigin(null);
    setShowSources(false);
    // Stage 7: send the sources count to finish onboarding
    if (!onboardingComplete) {
      triggerOnboardingStage(`${count} sources added`);
    }
  }, [onboardingComplete, triggerOnboardingStage]);

  /* ────────────────── Determine if input should be shown ────────────────── */

  // Stage 1: intro gate (user can ask a question instead of clicking "Yes, let's go!").
  // Stages 4 & 5: role and goals. Stage 6: corrections to what Elliott will remember
  // (or "Looks great!"). Once onboarding is done, input is available to start chatting.
  const showOnboardingInput =
    view === 'onboarding' &&
    (onboardingComplete || [1, 3, 4, 5, 6].includes(onboardingStage));

  /* ────────────────── Heuristic: "ready" button on assistant messages ────────────────── */

  const shouldShowReadyButton = (content: string) =>
    content.toLowerCase().includes('ready to get started') ||
    content.toLowerCase().includes('ready to jump in') ||
    content.toLowerCase().includes('are you ready');

  const shouldShowConfirmButton = (content: string) =>
    content.toLowerCase().includes('let me know if there is anything we should change') ||
    content.toLowerCase().includes("once we're aligned");

  // Key the "View Knowledge" button on the sources message itself, not the stage,
  // so it never leaks onto the profile-confirmation step on resume.
  const shouldShowSourcesButton = (content: string) =>
    content.toLowerCase().includes('how to add sources') ||
    content.toLowerCase().includes('adding internal documents');

  /* ────────────────── Render helpers ────────────────── */

  const renderThumbRating = (message: CharlieMessage) => {
    if (!message.logId) return null;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, [MOBILE_SX]: { mt: 0, ml: -1.25 } }}>
        <DosenTooltip title="Good response">
          <IconButton
            size="small"
            onClick={() => handleThumbRating(message.id, message.logId!, 'up')}
            sx={{ color: message.thumbRating === 'up' ? '#4CAF50' : '#868686', p: 0.5, [MOBILE_SX]: { p: 1.25 } }}
          >
            {message.thumbRating === 'up' ? (
              <ThumbUpAltIcon sx={{ fontSize: 16 }} />
            ) : (
              <ThumbUpOffAltIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </DosenTooltip>
        <DosenTooltip title="Bad response">
          <IconButton
            size="small"
            onClick={() => handleThumbRating(message.id, message.logId!, 'down')}
            sx={{ color: message.thumbRating === 'down' ? '#F44336' : '#868686', p: 0.5, [MOBILE_SX]: { p: 1.25 } }}
          >
            {message.thumbRating === 'down' ? (
              <ThumbDownAltIcon sx={{ fontSize: 16 }} />
            ) : (
              <ThumbDownOffAltIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </DosenTooltip>
      </Box>
    );
  };

  const renderDocRow = (doc: OrgDocument, ready: boolean, isPending = false) => {
    const badgeLabel =
      doc.type === 'webpage' ? 'URL' : (doc.fileType?.split('/')?.pop() || doc.type || '').toUpperCase();
    return (
      <Box key={doc._id} display="flex" flexDirection="column" gap={1.5} sx={{ opacity: ready ? 1 : 0.6 }}>
        <Box
          display="inline-flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          sx={{ [MOBILE_SX]: { gap: 1 } }}
        >
          <Box display="flex" alignItems="center" gap={1.25} sx={{ [MOBILE_SX]: { minWidth: 0, flex: 1 } }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                p: '2px 4px',
                [MOBILE_SX]: { flexShrink: 0 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '4px',
                  border: '2px solid var(--Stroke-icon-dim, #868686)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {[3, 8, 13].map((top) => (
                  <Box
                    key={top}
                    sx={{
                      position: 'absolute',
                      top,
                      left: 3,
                      right: 3,
                      height: 2,
                      background: 'var(--Stroke-icon-dim, #868686)'
                    }}
                  />
                ))}
              </Box>
            </Box>
            {/* Phone: a long title wraps on its own lines instead of overlapping (14px line height). */}
            <Typography
              sx={{
                color: '#FDFDFD',
                fontSize: 16,
                fontWeight: 500,
                lineHeight: '14px',
                [MOBILE_SX]: { minWidth: 0, fontSize: 15, lineHeight: '20px', overflowWrap: 'anywhere' }
              }}
            >
              {doc.title}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: '5px',
                [MOBILE_SX]: { flexShrink: 0 },
                background: 'var(--gray-700, #444444)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                sx={{
                  color: '#FDFDFD',
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  lineHeight: '14px'
                }}
              >
                {badgeLabel}
              </Typography>
            </Box>
          </Box>
          {isPending && (
            <Chip
              label="Processing"
              size="small"
              sx={{ bgcolor: 'rgba(247, 148, 32, 0.12)', color: '#F79420', fontWeight: 600 }}
            />
          )}
          {!isPending && doc.documentProcessingStatus === 'failed' && (
            <Chip
              label="Failed"
              size="small"
              sx={{ bgcolor: 'rgba(244, 67, 54, 0.12)', color: '#F44336', fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>
    );
  };

  /* ────────────────── Render: message bubble ────────────────── */

  const lastAssistantIndex = useMemo(
    () => messageList.reduce((acc, msg, i) => (msg.role === 'assistant' ? i : acc), -1),
    [messageList]
  );

  const renderMessageBubble = (message: CharlieMessage, index: number) => {
    const isLast = index === messageList.length - 1;
    const isAssistant = message.role === 'assistant';
    const isLastAssistant = isAssistant && index === lastAssistantIndex;
    const isOnboarding = view === 'onboarding' && !onboardingComplete;

    // Assistant messages use the lesson's Elliott treatment: orb avatar +
    // gradient ELLIOTT label, copy flush to the column edge (no bubble).
    if (isAssistant) {
      // Intro delivers two assistant messages (welcome + "are you ready"); the
      // "Hi, I'm Elliott." title belongs only on the first (the welcome).
      const isFirstStage = isOnboarding && onboardingStage <= 1 && index === 0;

      return (
        <Box
          key={message.id}
          ref={isLastAssistant ? lastResponseRef : undefined}
          sx={{
            width: '100%',
            maxWidth: 768,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            // Phone: the copy keeps the orb column but drops the 68px right gutter, so it runs the full width.
            [MOBILE_SX]: { '& > :first-of-type': { pr: 0, gap: '12px' } }
          }}
        >
          <ElliottMessage>
            {isFirstStage && (
              <Typography
                sx={{
                  fontSize: 30,
                  fontWeight: 500,
                  color: t.textPrimary,
                  lineHeight: '40px',
                  [MOBILE_SX]: { fontSize: 24, lineHeight: '30px' }
                }}
              >
                Hi, I&apos;m Elliott.
              </Typography>
            )}
            <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>{renderFormattedText(message.content)}</Box>
            {onboardingComplete && renderThumbRating(message)}
          </ElliottMessage>

          {/* Action buttons */}
          {isOnboarding && isLast && !isSending && (
            <>
              {shouldShowReadyButton(message.content) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <DosenButton
                    sizeVariant="sm"
                    disabled={!!disabledActionButtons[`ready_${message.id}`] || isSending}
                    onClick={() =>
                      pendingIntroContext
                        ? revealPendingIntro()
                        : sendOnboardingMessage("Yes, let's go!", { disableKey: `ready_${message.id}` })
                    }
                  >
                    Yes, Let&apos;s Go!
                  </DosenButton>
                </Box>
              )}

              {shouldShowConfirmButton(message.content) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <DosenButton
                    sizeVariant="sm"
                    disabled={!!disabledActionButtons[`confirm_${message.id}`] || isSending}
                    onClick={() =>
                      sendOnboardingMessage('Looks great!', { disableKey: `confirm_${message.id}` })
                    }
                  >
                    Looks Great!
                  </DosenButton>
                </Box>
              )}

              {shouldShowSourcesButton(message.content) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <DosenButton
                    sizeVariant="sm"
                    disabled={!!disabledActionButtons['sources_stage5']}
                    onClick={() => {
                      setDisabledActionButtons((prev) => ({ ...prev, sources_stage5: true }));
                      addedSourceIdsRef.current = new Set();
                      setSourcesOrigin('onboarding');
                      setShowSources(true);
                    }}
                  >
                    View Knowledge
                  </DosenButton>
                </Box>
              )}
            </>
          )}
        </Box>
      );
    }

    // The user's own messages — right-aligned brandy outline bubble with the
    // learner avatar, exactly as in the lesson thread.
    return (
      <Box
        key={message.id}
        // Phone: a 40px lead instead of 130px, so the bubble isn't squeezed into half the screen.
        sx={{ width: '100%', maxWidth: 768, [MOBILE_SX]: { '& > :first-of-type': { pl: '40px', gap: '10px' } } }}
      >
        <LearnerMessage>{message.content}</LearnerMessage>
      </Box>
    );
  };


  /* ────────────────── Render: chat input bar ────────────────── */

  // `locked`: a stage where Elliott leads (the learner clicks a button instead) —
  // the bar stays on screen in the lesson's locked treatment rather than vanishing.
  const renderChatInput = (opts: { placeholder: string; onSend: (text: string) => void; locked?: boolean }) => (
    <Box sx={{ position: 'relative', pt: 1.5 }}>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: -80,
          height: 80,
          background: `linear-gradient(180deg, rgba(15,15,15,0) 0%, ${t.bg} 85%)`,
          pointerEvents: 'none'
        }}
      />
      {/* Same bar as the Elliott lesson (brandy outline + glow). No mic here —
          Elliott Conversations has no voice input yet. */}
      <InputBar
        value={input}
        onChange={setInput}
        onSend={() => opts.onSend(input)}
        disabled={isSending || !!opts.locked}
        placeholder={opts.placeholder}
      />
    </Box>
  );

  /* ────────────────── Render: Sources panel ────────────────── */

  const renderSourcesPanel = () => (
    <Box
      sx={{
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 3.75,
        pt: { xs: 4, md: 6 },
        [MOBILE_SX]: { pt: 0, gap: 2.5 }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFileIcon sx={{ color: '#FDFDFD' }} />
        <Typography sx={{ color: '#FDFDFD', fontSize: 20, fontWeight: 600, [MOBILE_SX]: { flex: 1 } }}>
          Knowledge
        </Typography>
        {/* Phone: the way out sits on the panel's own title row, not floated over it. */}
        {isMobileApp &&
          (sourcesOrigin === 'onboarding' ? (
            <DosenButton sizeVariant="sm" tone="basic" onClick={handleSourcesDone}>
              Done
            </DosenButton>
          ) : (
            onboardingComplete && (
              <DosenButton sizeVariant="sm" tone="basic" onClick={() => setShowSources(false)}>
                Previous
              </DosenButton>
            )
          ))}
      </Box>

      {/* Phone: the three ways to add knowledge stack as full-width rows. */}
      <Box sx={{ display: 'flex', gap: 1.875, width: '100%', [MOBILE_SX]: { flexDirection: 'column', gap: 1.25 } }}>
        <Box
          onClick={() => setOpenFileModal(true)}
          sx={{
            flex: 1,
            p: '12px 15px',
            borderRadius: '6px',
            outline: '1px solid #868686',
            outlineOffset: '-1px',
            display: 'flex',
            gap: 1.25,
            cursor: 'pointer'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                Add Files
              </Typography>
              <UploadFileIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
            </Box>
            <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
              Upload PDFs or images that should inform Elliott&apos;s answers.
            </Typography>
          </Box>
        </Box>

        <Box
          onClick={() => setOpenTextModal(true)}
          sx={{
            flex: 1,
            p: '12px 15px',
            borderRadius: '6px',
            outline: '1px solid #868686',
            outlineOffset: '-1px',
            display: 'flex',
            gap: 1.25,
            cursor: 'pointer'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                Add Text
              </Typography>
              <ArticleIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
            </Box>
            <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
              Paste or type content that Elliott should use.
            </Typography>
          </Box>
        </Box>

        <Box
          onClick={() => setOpenUrlModal(true)}
          sx={{
            flex: 1,
            p: '12px 15px',
            borderRadius: '6px',
            outline: '1px solid #868686',
            outlineOffset: '-1px',
            display: 'flex',
            gap: 1.25,
            cursor: 'pointer'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                Add URL
              </Typography>
              <LinkIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
            </Box>
            <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
              Share a webpage or online resource that should inform Elliott&apos;s answers.
            </Typography>
          </Box>
        </Box>
      </Box>

      {docsError && (
        <Typography color="error.main" sx={{ mb: 1 }}>
          {docsError}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {(['file', 'text', 'webpage'] as const).map((docType) => (
          <Accordion
            key={docType}
            defaultExpanded
            sx={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 1, overflow: 'hidden' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#B6B6B6' }} />}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 600, textTransform: 'capitalize' }}>
                {docType === 'file' ? 'Files' : docType === 'text' ? 'Text' : 'URLs'} (
                {orgDocs.filter((d) => d.type === docType).length +
                  pendingDocs.filter((d) => d.type === docType).length}
                )
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {loadingDocs ? (
                <Box aria-busy aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {Array.from({ length: 3 }, (_, i) => (
                    <Bone key={i} height={40} />
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {pendingDocs
                    .filter((d) => d.type === docType)
                    .map((doc) =>
                      renderDocRow(
                        {
                          _id: doc.id,
                          title: doc.name,
                          type: doc.type as any,
                          fileType: '',
                          fileName: doc.name,
                          createdAt: Date.now(),
                          documentProcessingStatus: 'processing',
                          uploadedUserId: ''
                        },
                        false,
                        true
                      )
                    )}
                  {orgDocs.filter((d) => d.type === docType).length === 0 &&
                    pendingDocs.filter((d) => d.type === docType).length === 0 && (
                      <Typography sx={{ color: '#868686' }}>
                        No {docType === 'file' ? 'files uploaded' : docType === 'webpage' ? 'URLs added' : 'text added'}{' '}
                        yet.
                      </Typography>
                    )}
                  {orgDocs
                    .filter((d) => d.type === docType)
                    .map((doc) =>
                      renderDocRow(
                        doc,
                        doc.documentProcessingStatus === 'indexed',
                        doc.documentProcessingStatus === 'processing'
                      )
                    )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <AddFileModal open={openFileModal} onClose={() => setOpenFileModal(false)} onUpload={handleFileUpload} />
      <AddTextModal open={openTextModal} onClose={() => setOpenTextModal(false)} onCreate={handleAddText} />
      <AddURLModal open={openUrlModal} onClose={() => setOpenUrlModal(false)} onCreate={handleAddUrl} />
    </Box>
  );

  /* ════════════════════════════ RENDER ════════════════════════════ */

  // Phone: a thread on screen owns it like a messaging app, messages scrolling
  // between the app bar and a composer that sits right on the tab bar.
  const threadOnScreen = !showSources && (view === 'chat' || view === 'onboarding');

  // "About me" + "Knowledge" / "Previous". Desktop floats them top right; the
  // phone puts them in the flow of the home and Knowledge screens.
  const topActions = (
    <>
      {!showSources && (
        // No hover on a phone: the tooltip would only linger over the thread after a tap.
        <DosenTooltip title={isMobileApp ? '' : 'What Elliott knows about me'}>
          {/* DosenButton doesn't forward refs, which Tooltip needs. */}
          <span>
            <DosenButton sizeVariant="sm" tone="basic" onClick={() => setShowProfile(true)}>
              About Me
            </DosenButton>
          </span>
        </DosenTooltip>
      )}
      <DosenButton sizeVariant="sm" tone="basic" onClick={() => setShowSources((prev) => !prev)}>
        {showSources ? 'Previous' : 'Knowledge'}
      </DosenButton>
    </>
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        backgroundColor: t.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // The scroll area's 24px bottom padding is for pages that scroll; a thread runs down to the tabs.
        [MOBILE_SX]: threadOnScreen ? { height: 'calc(100% + 24px)', minHeight: 0, mb: '-24px' } : {}
      }}
    >
      <Box
        sx={{
          width: '100%',
          flex: 1,
          px: { xs: 2, md: 3 },
          pt: { xs: 6, md: 10 },
          pb: 3.5,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          minHeight: 0,
          [MOBILE_SX]: { px: 0, pt: 0, pb: threadOnScreen ? 1.5 : 1 }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 880, flex: 1, minHeight: 0, display: 'flex' }}>
          {/* ── Loading ── */}
          {view === 'loading' && (
            <Box
              sx={{
                width: '100%',
                maxWidth: 600,
                mx: 'auto',
                pt: { xs: 6, md: 10 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'charliePulse 1.8s ease-in-out infinite',
                  '@keyframes charliePulse': {
                    '0%': { transform: 'scale(0.96)', opacity: 0.6 },
                    '50%': { transform: 'scale(1.04)', opacity: 1 },
                    '100%': { transform: 'scale(0.96)', opacity: 0.6 }
                  }
                }}
              >
                <ElliottFace size={56} glow={false} />
              </Box>
            </Box>
          )}

          {/* ── Sources panel ── */}
          {showSources && renderSourcesPanel()}

          {/* ── Conversation list (post-onboarding) ── */}
          {!showSources && view === 'index' && (
            <Box
              sx={{
                width: '100%',
                maxWidth: 600,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                pt: { xs: 6, md: 10 },
                [MOBILE_SX]: { pt: 0, gap: 2.5 }
              }}
            >
              {isMobileApp && onboardingComplete && !sourcesOrigin && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>{topActions}</Box>
              )}
              {/* Phone: the orb over a centred two-line greeting, like an assistant's welcome screen. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  width: '100%',
                  [MOBILE_SX]: { flexDirection: 'column', gap: 1.5, pt: 1 }
                }}
              >
                <ElliottFace size={44} />
                <Typography
                  component="div"
                  sx={{
                    textAlign: 'center',
                    fontSize: 30,
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    [MOBILE_SX]: { fontSize: 24, lineHeight: '32px' }
                  }}
                >
                  <Box component="span">Hi, I&apos;m </Box>
                  <Box component="span" sx={{ color: '#F15A26' }}>
                    Elliott
                  </Box>
                  <Box component="span">. Need help?</Box>
                </Typography>
              </Box>

              {/* New conversation input — same bar as the lesson thread. */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%' }}>
                <InputBar
                  value={input}
                  onChange={setInput}
                  onSend={() => handleStartNewConversation(input)}
                  disabled={isSending}
                  placeholder="Start a conversation..."
                />
                {/* InputBar's send only fires with text — keep the "open a blank
                    chat" path the old arrow button provided. */}
                <Box
                  component="button"
                  onClick={() => handleStartNewConversation()}
                  disabled={isSending}
                  sx={{
                    background: 'none',
                    border: 'none',
                    p: 0,
                    fontSize: 13,
                    fontFamily: 'Inter, sans-serif',
                    color: t.textTertiary,
                    cursor: isSending ? 'default' : 'pointer',
                    '&:hover': { color: t.brandLight },
                    [MOBILE_SX]: { minHeight: 40, px: 1.5 }
                  }}
                >
                  Open a blank conversation
                </Box>
              </Box>

              {/* Conversation list */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {isLoadingConversations && (
                  <Box
                    sx={{
                      py: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      animation: 'charliePulse 1.8s ease-in-out infinite',
                      '@keyframes charliePulse': {
                        '0%': { transform: 'scale(0.96)', opacity: 0.6 },
                        '50%': { transform: 'scale(1.04)', opacity: 1 },
                        '100%': { transform: 'scale(0.96)', opacity: 0.6 }
                      }
                    }}
                  >
                    <ElliottFace size={36} glow={false} />
                  </Box>
                )}
                {!isLoadingConversations &&
                  conversations.map((conversation, index) => {
                    const title = conversation.convTitle || conversation.title || 'Conversation';
                    const description = conversation.description || conversation.summary || '';
                    const dateLabel = formatConversationDate(conversation);
                    return (
                      <Box
                        key={`${title}-${index}`}
                        sx={{
                          px: 2,
                          py: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          borderBottom: '1px solid #2A2A2A',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#1A1A1A' },
                          [MOBILE_SX]: { px: 0.5, minHeight: 56, gap: 1.5, '&:active': { backgroundColor: '#1A1A1A' } }
                        }}
                        onClick={() => openConversation(conversation)}
                      >
                        <Box sx={{ flex: 1, [MOBILE_SX]: { minWidth: 0 } }}>
                          {/* Phone: the title and the summary wrap in full rather than cutting off. */}
                          <Typography
                            fontSize={14}
                            color="#E5E5E5"
                            fontWeight={700}
                            noWrap
                            sx={{ [MOBILE_SX]: { fontSize: 15, whiteSpace: 'normal', overflowWrap: 'anywhere' } }}
                          >
                            {title}
                          </Typography>
                          <Typography
                            fontSize={12}
                            color="#9A9A9A"
                            noWrap
                            sx={{
                              [MOBILE_SX]: {
                                fontSize: 13,
                                whiteSpace: 'normal',
                                overflow: 'visible',
                                overflowWrap: 'anywhere'
                              }
                            }}
                          >
                            {description}
                          </Typography>
                        </Box>
                        <Box sx={{ width: 80, [MOBILE_SX]: { width: 'auto', flexShrink: 0 } }}>
                          <Typography fontSize={12} color="#9A9A9A" textAlign="right" noWrap>
                            {dateLabel}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          )}

          {/* ── Onboarding view ── */}
          {!showSources && view === 'onboarding' && (
            <Box
              sx={{
                width: '100%',
                maxWidth: 768,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                gap: 3,
                [MOBILE_SX]: { gap: 0 }
              }}
            >
              {isMobileApp && onboardingComplete && !sourcesOrigin && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>{topActions}</Box>
              )}
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box
                  ref={chatScrollRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    pr: 1,
                    pb: 8,
                    // Phone: the thread fades out under the action row instead of cutting a line in half.
                    [MOBILE_SX]: {
                      pr: 0,
                      pt: 2,
                      pb: 3,
                      gap: 2.5,
                      WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 24px)',
                      maskImage: 'linear-gradient(to bottom, transparent 0, #000 24px)'
                    }
                  }}
                >
                  {messageList.map((message, index) => renderMessageBubble(message, index))}
                  {isSending && <CharlieThinkingLoader />}
                  <Box ref={chatBottomRef} />
                </Box>

                {/* Input is live during interactive stages and after onboarding
                    finishes; otherwise it stays visible but locked (lesson style). */}
                {renderChatInput({
                    locked: !showOnboardingInput,
                    placeholder:
                      onboardingComplete
                        ? 'Ask Elliott'
                        : onboardingStage === 4
                          ? 'Tell Elliott about yourself...'
                          : onboardingStage === 5
                            ? 'Tell Elliott about your goals...'
                            : onboardingStage === 6
                              ? 'Or tell Elliott what to change...'
                              : 'Type your response...',
                    onSend: (text) =>
                      onboardingComplete ? sendChatMessage(text) : sendOnboardingMessage(text)
                  })}
              </Box>
            </Box>
          )}

          {/* ── Chat view (post-onboarding) ── */}
          {!showSources && view === 'chat' && (
            <Box
              sx={{
                width: '100%',
                maxWidth: 768,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                gap: 3,
                [MOBILE_SX]: { gap: 0 }
              }}
            >
              {isMobileApp && onboardingComplete && !sourcesOrigin && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>{topActions}</Box>
              )}
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box
                  ref={chatScrollRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    pr: 1,
                    pb: 8,
                    // Phone: the thread fades out under the action row instead of cutting a line in half.
                    [MOBILE_SX]: {
                      pr: 0,
                      pt: 2,
                      pb: 3,
                      gap: 2.5,
                      WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 24px)',
                      maskImage: 'linear-gradient(to bottom, transparent 0, #000 24px)'
                    }
                  }}
                >
                  {isLoadingConversations ? (
                    <Box
                      sx={{
                        py: 6,
                        display: 'flex',
                        justifyContent: 'center',
                        animation: 'charliePulse 1.8s ease-in-out infinite',
                        '@keyframes charliePulse': {
                          '0%': { transform: 'scale(0.96)', opacity: 0.6 },
                          '50%': { transform: 'scale(1.04)', opacity: 1 },
                          '100%': { transform: 'scale(0.96)', opacity: 0.6 }
                        }
                      }}
                    >
                      <ElliottFace size={56} glow={false} />
                    </Box>
                  ) : (
                    <>
                      {messageList.map((message, index) => renderMessageBubble(message, index))}
                      {isSending && <CharlieThinkingLoader />}
                    </>
                  )}
                  <Box ref={chatBottomRef} />
                </Box>

                {renderChatInput({
                  placeholder: 'Ask Elliott',
                  onSend: sendChatMessage
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Top-right "About me" + "Knowledge" / "Back" buttons (the phone has them in the flow) ── */}
        {!isMobileApp && onboardingComplete && !sourcesOrigin && (
          <Box sx={{ position: 'absolute', right: { xs: 16, md: 24 }, top: 20, display: 'flex', gap: 1 }}>
            {topActions}
          </Box>
        )}

        <ElliottProfileDialog open={showProfile} onClose={() => setShowProfile(false)} location={location} />

        {/* Sources "Done" button during onboarding */}
        {!isMobileApp && sourcesOrigin === 'onboarding' && showSources && (
          <Box sx={{ position: 'absolute', right: { xs: 16, md: 24 }, top: 20 }}>
            <DosenButton sizeVariant="sm" tone="basic" onClick={handleSourcesDone}>
              Done
            </DosenButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Charlie;
